/**
 * HireConfirmationPage – Candidate accepts or declines a job offer
 * Also handles contract signing if a contract is attached
 */
import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { NavBar, NavActions } from "@/components/NavBar";
import { Footer } from "@/components/Footer";
import { toast } from "sonner";
import {
  CheckCircle,
  XCircle,
  MapPin,
  Calendar,
  DollarSign,
  User,
  Clock,
  Loader2,
  AlertTriangle,
  FileText,
  PenTool,
  Eraser,
} from "lucide-react";
import SignaturePad from "signature_pad";

export default function HireConfirmationPage() {
  const { hireId } = useParams();
  const navigate = useNavigate();
  const { token, user } = useAuth();

  const [hire, setHire] = useState(null);
  const [contract, setContract] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const [declining, setDeclining] = useState(false);

  // Contract signing state
  const [typedSignature, setTypedSignature] = useState("");
  const [consent, setConsent] = useState(false);
  const canvasRef = useRef(null);
  const sigPadRef = useRef(null);

  useEffect(() => {
    fetchHire();
  }, [hireId]);

  useEffect(() => {
    if (!canvasRef.current || !contract || contract.status === "signed") return;
    const canvas = canvasRef.current;
    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    canvas.width = canvas.offsetWidth * ratio;
    canvas.height = canvas.offsetHeight * ratio;
    canvas.getContext("2d").scale(ratio, ratio);
    sigPadRef.current = new SignaturePad(canvas, {
      backgroundColor: "rgb(255,255,255)",
      penColor: "rgb(0,0,0)",
    });
  }, [contract]);

  const fetchHire = async () => {
    try {
      const res = await fetch(`${API}/hiring/${hireId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        toast.error("Hire not found");
        navigate("/dashboard");
        return;
      }
      const data = await res.json();
      setHire(data);

      // Check for contract on this job
      if (data.job_id) {
        const cRes = await fetch(`${API}/contracts/for-job/${data.job_id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cRes.ok) {
          const cData = await cRes.json();
          if (cData.contract) setContract(cData.contract);
        }
      }
    } catch {
      toast.error("Failed to load");
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    // If there's a pending contract, sign it first
    if (contract && contract.status === "pending") {
      if (!typedSignature.trim()) return toast.error("Please type your full legal name to sign the contract");
      if (!consent) return toast.error("You must consent to sign electronically");

      let drawnSig = null;
      if (sigPadRef.current && !sigPadRef.current.isEmpty()) {
        drawnSig = sigPadRef.current.toDataURL("image/png");
      }

      try {
        const signRes = await fetch(`${API}/contracts/${contract.contract_id}/sign`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}`, "X-Timezone": Intl.DateTimeFormat().resolvedOptions().timeZone },
          body: JSON.stringify({ typed_signature: typedSignature.trim(), drawn_signature: drawnSig, consent: true }),
        });
        if (!signRes.ok) {
          const err = await signRes.json();
          toast.error(err.detail || "Failed to sign contract");
          return;
        }
      } catch {
        toast.error("Failed to sign contract");
        return;
      }
    }

    setConfirming(true);
    try {
      const res = await fetch(`${API}/hiring/confirm/${hireId}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        toast.success("Job accepted! The studio has been notified.");
        navigate(`/jobs/${hire.job_id}`);
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to confirm");
      }
    } catch {
      toast.error("Network error");
    } finally {
      setConfirming(false);
    }
  };

  const handleDecline = async () => {
    if (!window.confirm("Are you sure you want to decline this job? The studio will be notified and the job will be reopened for other candidates."))
      return;

    setDeclining(true);
    try {
      const res = await fetch(`${API}/hiring/decline/${hireId}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        toast.success("Job declined. The studio has been notified.");
        navigate("/dashboard");
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to decline");
      }
    } catch {
      toast.error("Network error");
    } finally {
      setDeclining(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a]">
        <NavBar />
        <div className="flex items-center justify-center pt-32">
          <Loader2 className="w-8 h-8 animate-spin text-white/40" />
        </div>
      </div>
    );
  }

  if (!hire) return null;

  const isPending = hire.status === "pending_confirmation";
  const isConfirmed = hire.status === "confirmed";
  const isDeclined = hire.status === "declined";
  const isWithdrawn = hire.status === "withdrawn";
  const isCandidate = user?.user_id === hire.candidate_id;

  const deadline = hire.confirmation_deadline
    ? new Date(hire.confirmation_deadline)
    : null;
  const isExpired = deadline && new Date() > deadline && isPending;

  // Format date (avoid timezone shift)
  let dateDisplay = "";
  if (hire.job_date) {
    try {
      const parts = hire.job_date.split("T")[0].split("-");
      const dt = new Date(+parts[0], +parts[1] - 1, +parts[2]);
      dateDisplay = dt.toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" });
    } catch {}
  }

  // Format rate - handle both old free-text and new structured format
  let rateDisplay = "";
  if (hire.final_rate) {
    // Strip any existing $ or unit text from the value
    let rateVal = String(hire.final_rate).replace(/^\$/, "").replace(/\s*\/?(hr|hour|flat|per\s*job)\s*/gi, "").trim();
    // Determine type from hire.rate_type or job_payment_type
    let type = hire.rate_type || hire.job_payment_type || "";
    rateDisplay = `$${rateVal}`;
    if (type === "hourly") rateDisplay += "/hr";
    else if (type === "flat") rateDisplay += " flat";
  } else if (hire.job_hourly_rate) {
    rateDisplay = `$${hire.job_hourly_rate}/hr`;
  } else if (hire.job_flat_rate) {
    rateDisplay = `$${hire.job_flat_rate} flat`;
  }

  // Format time
  let timeDisplay = "";
  if (hire.job_start_time) {
    timeDisplay = hire.job_start_time;
    if (hire.job_end_time) timeDisplay += ` – ${hire.job_end_time}`;
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white flex flex-col">
      <NavBar>
        <Link to="/dashboard" className="text-sm font-medium text-white/60 hover:text-white transition-colors uppercase tracking-wider">Dashboard</Link>
        <NavActions />
      </NavBar>
      <main className="flex-1 max-w-2xl mx-auto px-4 py-8 pt-24">
        <h1 className="text-2xl font-bold mb-6" data-testid="hire-confirm-title">
          {isPending ? "You've Been Hired!" : isConfirmed ? "Job Confirmed" : isDeclined ? "Job Declined" : "Job Offer"}
        </h1>

        {/* Status Banner */}
        {isConfirmed && (
          <div className="flex items-center gap-3 mb-6 p-4 bg-green-500/10 border border-green-500/20 rounded" data-testid="confirmed-banner">
            <CheckCircle className="w-6 h-6 text-green-400 shrink-0" />
            <div>
              <p className="font-semibold text-green-300">You accepted this job</p>
              <p className="text-white/50 text-sm">Confirmed on {new Date(hire.confirmed_at).toLocaleString()}</p>
            </div>
          </div>
        )}
        {isDeclined && (
          <div className="flex items-center gap-3 mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded">
            <XCircle className="w-6 h-6 text-red-400 shrink-0" />
            <p className="font-semibold text-red-300">You declined this job</p>
          </div>
        )}
        {isWithdrawn && (
          <div className="flex items-center gap-3 mb-6 p-4 bg-white/5 border border-white/10 rounded">
            <AlertTriangle className="w-6 h-6 text-amber-400 shrink-0" />
            <p className="font-semibold text-amber-300">The studio withdrew this offer</p>
          </div>
        )}

        {/* Job Details Card */}
        <Card className="bg-white/[0.03] border-white/10 p-6 mb-6" data-testid="job-info-card">
          <h2 className="text-lg font-bold mb-4">{hire.job_title || "Job"}</h2>
          <div className="space-y-3 text-sm">
            <div className="flex items-center gap-2 text-white/60">
              <User className="w-4 h-4" />
              <span>Studio: <strong className="text-white">{hire.studio_name}</strong></span>
            </div>
            {dateDisplay && (
              <div className="flex items-center gap-2 text-white/60">
                <Calendar className="w-4 h-4" />
                <span>{dateDisplay}</span>
              </div>
            )}
            {timeDisplay && (
              <div className="flex items-center gap-2 text-white/60">
                <Clock className="w-4 h-4" />
                <span>{timeDisplay}</span>
              </div>
            )}
            {hire.job_location && (hire.job_location.city || hire.job_location.address) && (
              <div className="flex items-center gap-2 text-white/60">
                <MapPin className="w-4 h-4" />
                <span>{[hire.job_venue_name, hire.job_venue_address, hire.job_location.city, hire.job_location.state].filter(Boolean).join(", ")}</span>
              </div>
            )}
            {rateDisplay && (
              <div className="flex items-center gap-2 text-white/60">
                <DollarSign className="w-4 h-4" />
                <span>Rate: <strong className="text-white">{rateDisplay}</strong></span>
              </div>
            )}
            {hire.hire_message && (
              <div className="mt-3 p-3 bg-white/5 rounded border-l-2 border-[#FF6B6B]">
                <p className="text-xs text-white/40 mb-1">Message from {hire.studio_name}:</p>
                <p className="text-white/80 italic">"{hire.hire_message}"</p>
              </div>
            )}
          </div>
        </Card>

        {/* Accept/Decline for pending */}
        {isPending && !isExpired && isCandidate && (
          <Card className="bg-[#FF6B6B]/5 border-[#FF6B6B]/20 p-6 mb-6" data-testid="accept-rate-card">
            <p className="text-white/70 text-sm mb-2">By accepting, you confirm:</p>
            <ul className="text-sm text-white/80 space-y-1 mb-0 list-disc pl-5">
              <li>You are available on <strong>{dateDisplay || "the scheduled date"}</strong></li>
              {rateDisplay && <li>You agree to the rate of <strong>{rateDisplay}</strong></li>}
              <li>You will fulfill the role as described</li>
            </ul>
          </Card>
        )}

        {/* Deadline Warning */}
        {isPending && deadline && !isExpired && (
          <div className="flex items-center gap-2 mb-6 p-3 bg-amber-500/10 border border-amber-500/20 rounded text-sm" data-testid="deadline-warning">
            <Clock className="w-4 h-4 text-amber-400 shrink-0" />
            <span className="text-amber-300">
              Please respond by {deadline.toLocaleString("en-US", { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
            </span>
          </div>
        )}

        {/* Contract Section */}
        {contract && isPending && isCandidate && contract.status === "pending" && (
          <Card className="bg-white/[0.03] border-white/10 p-6 mb-6" data-testid="contract-section">
            <h3 className="font-semibold mb-3 flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#FF6B6B]" /> Contract: {contract.template_title}
            </h3>
            <div className="bg-white rounded p-6 mb-4 max-h-[400px] overflow-y-auto">
              <div className="prose prose-sm max-w-none text-black contract-view" dangerouslySetInnerHTML={{ __html: contract.content }} />
            </div>

            {/* Signature Fields */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1.5">Type your full legal name</label>
                <Input
                  value={typedSignature}
                  onChange={(e) => setTypedSignature(e.target.value)}
                  placeholder="Your full legal name"
                  className="bg-white/5 border-white/10 text-white text-lg"
                  data-testid="typed-signature-input"
                />
                {typedSignature && <p className="mt-2 text-xl font-serif italic text-white/80">{typedSignature}</p>}
              </div>

              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="block text-sm font-medium">Draw your signature (optional)</label>
                  <Button type="button" variant="ghost" size="sm" onClick={() => sigPadRef.current?.clear()} className="text-white/50 text-xs">
                    <Eraser className="w-3 h-3 mr-1" /> Clear
                  </Button>
                </div>
                <div className="border border-white/10 rounded overflow-hidden bg-white">
                  <canvas ref={canvasRef} className="w-full" style={{ height: "100px", touchAction: "none" }} data-testid="signature-canvas" />
                </div>
              </div>

              <div className="flex items-start gap-3 p-3 bg-white/[0.02] border border-white/10 rounded">
                <Checkbox
                  id="consent"
                  checked={consent}
                  onCheckedChange={setConsent}
                  className="mt-0.5 border-white/30 data-[state=checked]:bg-[#FF6B6B] data-[state=checked]:border-[#FF6B6B]"
                  data-testid="consent-checkbox"
                />
                <label htmlFor="consent" className="text-xs text-white/70 leading-relaxed cursor-pointer">
                  I agree to sign this contract electronically. My electronic signature has the same legal effect as a handwritten signature under the ESIGN Act and UETA.
                </label>
              </div>
            </div>
          </Card>
        )}

        {/* Action Buttons */}
        {isPending && isCandidate && !isExpired && (
          <div className="flex gap-4" data-testid="action-buttons">
            <Button
              onClick={handleDecline}
              disabled={declining || confirming}
              variant="outline"
              className="flex-1 border-red-500/30 text-red-400 hover:bg-red-500/10 hover:text-red-300 py-6"
              data-testid="decline-btn"
            >
              {declining ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <XCircle className="w-5 h-5 mr-2" />}
              Decline
            </Button>
            <Button
              onClick={handleConfirm}
              disabled={confirming || declining || (contract && contract.status === "pending" && (!typedSignature.trim() || !consent))}
              className="flex-1 bg-[#3498DB] hover:bg-[#2980B9] text-white font-bold py-6 text-base"
              data-testid="accept-btn"
            >
              {confirming ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <CheckCircle className="w-5 h-5 mr-2" />}
              {contract && contract.status === "pending" ? "Sign & Accept Job" : "Accept Job"}
            </Button>
          </div>
        )}

        {isPending && isExpired && (
          <div className="p-4 bg-red-500/10 border border-red-500/20 rounded text-center">
            <p className="text-red-300 font-medium">This offer has expired.</p>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
